




















































import QuizBase from "@tt-app/components/exercise/QuizBase";
import { ExerciseType, Trophy } from "@tt-app/models";
import { getAuthInfo } from "@tt-app/utils";
import { api } from "@tt-app/api";
import { EXERCISE_TYPE } from "@tt-app/constants";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";
import { audioContext } from "@tt-app/utils/audio/audioContext";

import AssignmentCompletionModal from "@tt-app/components/assignment/AssignmentCompletionModal.vue";
import ExerciseHeaderTitle from "@tt-app/components/exercise/ExerciseHeaderTitle.vue";
import FeedbackPopup from "@tt-app/components/feedback/FeedbackPopup.vue";
import LayoutExercise from "@tt-app/layouts/LayoutExercise.vue";
import ListeningCard from "@tt-app/components/quiz/ListeningCard.vue";
import PictureMatchMultipleChoice from "@tt-app/components/exercise/PictureMatchMultipleChoice.vue";
import PictureMatchReview from "@tt-app/components/exercise/PictureMatchReview.vue";
import {
  Session,
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";

interface Data {
  answerSubmitted: boolean;
  currentPageIndex: number;
  exercise?: {
    header?: {};
    pages?: any[];
    type?: number;
  };
  exerciseResult: any;
  exerciseType: ExerciseType;
  feedbackFormVisible: boolean;
  logId?: number;
  nav: {
    requireExitConfirmation: boolean;
  };
  review: any;
  selectedOptionIndex?: number;
}

interface Methods {
  component: (componentConfig: any) => string | undefined;
  endExercise: () => void;
  handleFeedbackClose: () => void;
  logExerciseResponse: () => void;
  refreshAudioPlayer: () => void;
  scrollToTop: () => void;
  selectOption: (selectedOptionIndex: number) => void;
  startExercise: () => void;
}

interface Computed {
  accuracy: any;
  body: any[];
  currentPage: any;
  finalQuestion: any;
  footer: any;
  header: any;
  isReview: boolean;
  problemUuid: any;
  showFeedbackForm: boolean;
  selectedOptionIndex: any;
  session: Session;
  totalQuestions: number;
  trophy?: Trophy;
}

export default QuizBase.extend<Data, Methods, Computed>({
  name: "PictureMatch",
  components: {
    AssignmentCompletionModal,
    FeedbackPopup,
    ExerciseHeaderTitle,
    LayoutExercise,
    ListeningCard,
    PictureMatchMultipleChoice,
    PictureMatchReview
  },
  data() {
    return {
      answers: {},
      answerSubmitted: false,
      audioDecoderPlayer: new AudioDecoderPlayer({ audioContext }),
      currentPageIndex: 0,
      exercise: undefined,
      exerciseResult: undefined,
      exerciseType: EXERCISE_TYPE.PICTURE_MATCH,
      feedbackFormVisible: true,
      logId: undefined,
      nav: {
        requireExitConfirmation: false
      },
      review: undefined,
      selectedOption: undefined
    };
  },
  created() {
    document.addEventListener("visibilitychange", this.refreshAudioPlayer);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.refreshAudioPlayer);
  },
  computed: {
    accuracy() {
      let numCorrectAnswers = 0;
      let numIncorrectAnswers = 0;

      const answers = this.answers;

      Object.values(answers).forEach((answer: any) => {
        if (answer.isCorrect) {
          numCorrectAnswers += 1;
        } else {
          numIncorrectAnswers += 1;
        }
      });

      return {
        numCorrectAnswers,
        numIncorrectAnswers
      };
    },
    body() {
      if (!this.currentPage) {
        return undefined;
      }

      return this.currentPage.body;
    },
    currentPage() {
      return (
        (this.exercise && this.exercise.pages && this.exercise.pages[this.currentPageIndex]) ||
        undefined
      );
    },
    finalQuestion() {
      if (!this.currentPage) {
        return false;
      }

      const nextPage = this.exercise.pages[this.currentPageIndex + 1];

      return !!(nextPage && nextPage.body[0].type === "REVIEW");
    },
    footer() {
      if (!this.currentPage) {
        return {};
      }
      const isReview = this.currentPage.body[0].type === "REVIEW";

      let footerDefinition = {
        buttons: [
          {
            action: "NEXT",
            color: "primary",
            text: this.finalQuestion
              ? this.$__("exercise.action.review")
              : this.$__("exercise.action.next")
          }
        ]
      };

      if (this.isReview) {
        footerDefinition = this.review.footer[0];

        const buttons = footerDefinition.buttons;

        const nextExerciseButton = buttons.find(button => {
          return button.action === "NEXT_EXERCISE";
        });

        if (nextExerciseButton) {
          nextExerciseButton.text = this.nextExerciseButtonText;
        }
      }

      const props = {
        disabled: isReview ? false : !this.selectedOption || this.answerSubmitted
      };

      return { ...footerDefinition, ...props };
    },
    header() {
      return {
        courseTitle: this.course?.title,
        chapterTitle: this.chapter?.title,
        exerciseTitle: this.exercise?.title,
        sectionTitle: this.section?.title
      };
    },
    isReview() {
      if (!this.currentPage) return false;

      return this.currentPage.body[0].type === "REVIEW";
    },
    problemUuid() {
      if (!this.body) {
        return;
      }

      return this.body[2].problemUuid;
    },
    selectedOptionIndex() {
      return this.selectedOption && this.selectedOption.id;
    },
    session() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.ACTIVE_SESSION}`
      ];
    },
    showFeedbackForm() {
      return this.feedbackFormVisible && this.isReview;
    },
    totalQuestions() {
      return this.exercise.pages.filter(({ body }: any) => {
        const card = body.find((element: any) => {
          return element.type === "CARD";
        });

        let problem = false;
        if (card) {
          problem = card.body.find((element: any) => {
            return element.type === "IMAGE_SINGLE_SELECTION";
          });
        }

        return Boolean(problem);
      }).length;
    },
    trophy() {
      if (!this.exerciseResult) {
        return undefined;
      }
      const { numIncorrectAnswers, numCorrectAnswers } = this.accuracy;

      const trophyComments: { [key: string]: string } = {
        3: "ex.trophy.gold",
        2: "ex.trophy.silver",
        1: "ex.trophy.bronze"
      };

      const comment = this.$__(
        trophyComments[this.exerciseResult.trophy],
        this.exerciseResult.moreCorrectAnswers
      );

      return {
        comment,
        correctCount: numCorrectAnswers,
        incorrectCount: numIncorrectAnswers,
        score: this.exerciseResult.trophy
      };
    }
  },
  methods: {
    component(componentConfig) {
      if (!componentConfig) {
        return;
      }

      const components = {
        CARD: "picture-match-multiple-choice",
        CHAT_TEXT: "listening-card",
        REVIEW: "picture-match-review"
      };

      const { type }: { type: "CHAT_TEXT" } = componentConfig;

      return components[type];
    },
    async endExercise() {
      const authInfo = await getAuthInfo(this.$store.getters);

      const params = {
        ...authInfo,
        logId: this.logId,
        ...this.accuracy,
        assignmentId: Number(this.assignmentId) || undefined,
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType
      };

      const response: any = await api.exercise.endExercise(params);
      const { reviewPage, ...results } = response;

      this.review = reviewPage;
      this.exerciseResult = results;
      this.nav.requireExitConfirmation = false;
    },
    handleFeedbackClose() {
      this.feedbackFormVisible = false;
    },
    async logExerciseResponse() {
      this.answerSubmitted = true;

      const params = {
        logId: this.logId,
        problemUuid: this.selectedOption.problemUuid,
        answer: this.selectedOption.id,
        assignmentId: Number(this.assignmentId) || undefined,
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType
      };

      const authInfo = await getAuthInfo(this.$store.getters);

      const payload: any = {
        ...authInfo,
        ...params
      };

      const answer: any = await api.exercise.submitAnswer(payload);

      this.answers = {
        ...this.answers,
        [this.selectedOption.problemUuid]: { ...answer, selectedId: this.selectedOption.id }
      };

      if (this.finalQuestion) {
        await this.endExercise();
      }

      this.answerSubmitted = false;
    },
    refreshAudioPlayer() {
      if (document.visibilityState === "visible") {
        console.log("refreshing audio player");
        this.audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });
      }
    },
    scrollToTop() {
      const container = document.getElementById("drill-contents");
      container && container.scrollTo(0, 0);
    },
    selectOption(selectedOption: any) {
      this.selectedOption = selectedOption;
    },
    async startExercise() {
      const authInfo = await getAuthInfo(this.$store.getters);
      const details = {
        assignmentId: Number(this.assignmentId) || undefined,
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType,
        limitedModeId: this.session?.limitedMode?.id,
        sessionId: this.session?.id
      };

      const apiPayload = { ...authInfo, ...details };

      const response: any = await api.exercise.startExercise(apiPayload);

      this.logId = response.logId;
      this.nav.requireExitConfirmation = true;
    }
  },
  watch: {
    exercise() {
      this.startExercise();
    }
  }
});
